import { getEnvByHostname } from '../utils/env';
import React, { useState } from 'react';
import { Form, Input, Button, Select, DatePicker, Table, message, Card, Space, Tag, Row, Col } from 'antd';
import { SearchOutlined, ReloadOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { testApi } from '../services/api';
import { buildChannelOptions } from '../utils/channelLogos';
import dayjs from 'dayjs';
import { useLanguage } from '../contexts/LanguageContext';

const { RangePicker } = DatePicker;
const { Option } = Select;

const OrderList = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const { t } = useLanguage();
  const [userChannels, setUserChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);

  const orderTypeOptions = [
    { label: t('deposit') || '充值', value: 'DEPOSIT' },
    { label: t('withdraw') || '提现', value: 'WITHDRAW' },
  ];

  const depositStatusOptions = [
    { label: 'INIT', value: 'INIT' },
    { label: 'PROCESSING', value: 'PROCESSING' },
    { label: 'SUCCESS', value: 'SUCCESS' },
    { label: 'FAILED', value: 'FAILED' },
  ];

  const withdrawStatusOptions = [
    { label: 'PROCESSING', value: 'PROCESSING' },
    { label: 'SUCCESS', value: 'SUCCESS' },
    { label: 'FAILED', value: 'FAILED' },
  ];

  const [statusOptions, setStatusOptions] = useState(depositStatusOptions);

  React.useEffect(() => {
    loadUserChannels();
  }, []);

  const loadUserChannels = async () => {
    try {
      const { data } = await testApi.getChannelCurrencies();
      const responseData = data.data;
      const channelCurrencyMap = responseData?.responseBody || responseData || {};
      const channels = Object.keys(channelCurrencyMap).map(channelCode => ({
        channelCode,
        currencies: [...new Set(channelCurrencyMap[channelCode] || [])]
      }));
      
      setUserChannels(channels);
      if (channels.length > 0) {
        const firstChannel = channels[0];
        form.setFieldsValue({ 
          channelCode: firstChannel.channelCode,
          currency: firstChannel.currencies[0]
        });
        setSelectedChannel(firstChannel.channelCode);
      }
    } catch (error) {
      console.error('Failed to load user channels:', error);
      setUserChannels([]);
    }
  };

  const handleChannelChange = (channelCode) => {
    setSelectedChannel(channelCode);
    const channel = userChannels.find(c => c.channelCode === channelCode);
    if (channel && channel.currencies.length > 0) {
      form.setFieldsValue({ currency: channel.currencies[0] });
    }
  };

  const channelOptions = [
    { label: '🇧🇷 SGB', value: 'SGB' },
    { label: '🇬🇧 EQUALS_MONEY', value: 'EQUALS_MONEY' },
    { label: '🇨🇱 LEAN_TECH', value: 'LEAN_TECH' },
    { label: '🇳🇬 KORAPAY', value: 'KORAPAY' },
    { label: '🇮🇳 DURIAN_PAY', value: 'DURIAN_PAY' },
    { label: '🇱🇮 BANK_FRICK', value: 'BANK_FRICK' },
    { label: '🇱🇺 BANKING_CIRCLE', value: 'BANKING_CIRCLE' },
  ];

  const currencyOptions = [
    { label: 'BRL', value: 'BRL' },
    { label: 'NGN', value: 'NGN' },
    { label: 'USD', value: 'USD' },
    { label: 'EUR', value: 'EUR' },
    { label: 'GBP', value: 'GBP' },
    { label: 'INR', value: 'INR' },
  ];

  const handleOrderTypeChange = (value) => {
    form.setFieldsValue({ orderStatusList: undefined });
    setStatusOptions(value === 'DEPOSIT' ? depositStatusOptions : withdrawStatusOptions);
  };

  const handleSearch = async (values) => {
    setLoading(true);
    try {
      const { timeRange, ...rest } = values;
      const requestData = {
        requestId: `order_query_${Date.now()}`,
        channelCode: values.channelCode,
        filters: {
          orderType: values.orderType,
          currency: values.currency,
          orderStatusList: values.orderStatusList || undefined,
          externalUserId: values.externalUserId || undefined,
        },
        timeRange: {
          startTime: timeRange[0].valueOf(),
          endTime: timeRange[1].valueOf(),
        },
        pagination: {
          page: pagination.current,
          pageSize: pagination.pageSize,
        },
      };

      const testRequest = {
        testType: 'ORDER_LIST_QUERY',
        channelCode: values.channelCode,
        businessPartner: values.businessPartner || 'DEMO_ACCOUNT',
        currency: values.currency,
        env: getEnvByHostname(),
        executor: localStorage.getItem('username'),
        requestBody: JSON.stringify(requestData)
      };

      const response = await testApi.executeTest(testRequest);

      if (response.data.code === 200 || response.data.code === '200') {
        const { list, total, page, pageSize } = response.data.data;
        setDataSource(list);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: total,
        });
        message.success(t('querySuccess'));
      } else {
        message.error(response.data.msg || t('queryFailed') || '查询失败');
      }
    } catch (error) {
      message.error(error.response?.data?.msg || t('queryFailed') || '查询失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    form.submit();
  };

  const columns = [
    {
      title: t('orderId') || '订单ID',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 200,
    },
    {
      title: t('businessType') || '业务类型',
      dataIndex: 'businessType',
      key: 'businessType',
      width: 120,
      render: (text) => (
        <Tag color={text === 'DEPOSIT' ? 'green' : 'blue'}>{text}</Tag>
      ),
    },
    {
      title: t('channel') || '渠道',
      dataIndex: 'channelCode',
      key: 'channelCode',
      width: 100,
    },
    {
      title: t('currency') || '币种',
      dataIndex: 'currency',
      key: 'currency',
      width: 80,
    },
    {
      title: t('amount') || '金额',
      dataIndex: 'transAmount',
      key: 'transAmount',
      width: 120,
      render: (text) => parseFloat(text).toFixed(2),
    },
    {
      title: t('status') || '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (text) => {
        const color = text === 'SUCCESS' ? 'green' : text === 'FAILED' ? 'red' : 'orange';
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: t('externalUserId') || '外部用户ID',
      dataIndex: 'externalUserId',
      key: 'externalUserId',
      width: 150,
    },
    {
      title: t('accountId') || '账户ID',
      dataIndex: 'accountId',
      key: 'accountId',
      width: 150,
    },
    {
      title: t('businessPartner') || '业务伙伴',
      dataIndex: 'businessPartner',
      key: 'businessPartner',
      width: 120,
    },
    {
      title: t('transTime') || '交易时间',
      dataIndex: 'transTime',
      key: 'transTime',
      width: 180,
      render: (text) => dayjs(parseInt(text)).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: t('refund') || '退款',
      dataIndex: 'isRefund',
      key: 'isRefund',
      width: 80,
      render: (text) => (text ? <Tag color="orange">{t('yes') || '是'}</Tag> : <Tag>{t('no') || '否'}</Tag>),
    },
  ];

  return (
    <div style={{ padding: '16px 24px', height: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <div className="fade-in-up">
        {/* 顶部工具栏 */}
        <div style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '12px 24px',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
        }}>
          <Space size={12}>
            <UnorderedListOutlined style={{ color: '#fff', fontSize: 20 }} />
            <span style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>{t('orderListQuery') || '订单列表查询'}</span>
          </Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => { form.resetFields(); setDataSource([]); setPagination({ current: 1, pageSize: 20, total: 0 }); }}
            type="text"
            style={{ color: '#fff' }}
          >
            {t('refresh') || '重置'}
          </Button>
        </div>

        {/* 查询表单 */}
        <Card className="modern-card" bodyStyle={{ padding: '24px' }} style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>
            ⚡ {t('queryConditions')}
          </div>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSearch}
            initialValues={{
              orderType: 'DEPOSIT',
              currency: 'USD',
              timeRange: [dayjs().subtract(7, 'days'), dayjs()],
            }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item
                  label={t('channelCode') || '渠道代码'}
                  name="channelCode"
                  rules={[{ required: true, message: t('pleaseInputChannelCode') || '请选择渠道代码' }]}
                >
                  <Select 
                    placeholder={t('selectChannel') || '选择渠道'}
                    options={buildChannelOptions(userChannels.map(c => c.channelCode))}
                    onChange={handleChannelChange}
                    showSearch
                    filterOption={(input, option) =>
                      option.value.toLowerCase().includes(input.toLowerCase()) ||
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item
                  label={t('orderType') || '订单类型'}
                  name="orderType"
                  rules={[{ required: true }]}
                >
                  <Select onChange={handleOrderTypeChange}>
                    {orderTypeOptions.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item
                  label={t('currency') || '币种'}
                  name="currency"
                  rules={[{ required: true }]}
                >
                  <Select 
                    placeholder={t('selectCurrency') || '选择币种'}
                    options={(userChannels.find(c => c.channelCode === selectedChannel)?.currencies || []).map(c => ({ label: c, value: c }))}
                    showSearch
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label={t('orderStatus') || '订单状态'} name="orderStatusList">
                  <Select mode="multiple" placeholder={t('allStatuses') || '全部状态'} allowClear>
                    {statusOptions.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label={t('externalUserId') || '外部用户ID'} name="externalUserId">
                  <Input placeholder={t('optional') || '可选'} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={8}>
                <Form.Item
                  label={t('timeRange') || '时间范围'}
                  name="timeRange"
                  rules={[{ required: true, message: t('pleaseSelectTimeRange') || '请选择时间范围' }]}
                >
                  <RangePicker showTime style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} lg={4}>
                <Form.Item label=" ">
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    icon={<SearchOutlined />} 
                    loading={loading}
                    block
                    className="primary-button"
                    style={{ height: 40 }}
                  >
                    {t('search') || '查询'}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        {/* 结果表格 */}
        <Card className="modern-card" bodyStyle={{ padding: '24px' }}>
          <div style={{ 
            fontSize: 14, 
            fontWeight: 600, 
            marginBottom: 16, 
            color: '#374151',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>📊 {t('queryResults')}</span>
            {pagination.total > 0 && (
              <Tag color="blue">{t('totalCount', pagination.total)}</Tag>
            )}
          </div>
          <Table
            columns={columns}
            dataSource={dataSource}
            rowKey="orderId"
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            scroll={{ x: 1500 }}
          />
        </Card>
      </div>
    </div>
  );
};

export default OrderList;
